import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import { ADDITIONAL_ELEMENT_SUFFIX } from 'components/WorkspaceEditor/utils'
import React, { memo, useMemo } from 'react'
import { EmptyPlainTextContainer } from '../EmptyPlainTextContainer'
import { EditorContent } from './EditorContent'
import { usePlainTextController } from './hooks/usePlainTextController'
import { usePlainTextStyles } from './usePlainTextStyles'

// Utility functions
const generateSuggestionDataAttribute = (suggestionId?: string) => {
    return suggestionId ? `content-suggestion-id-${suggestionId}` : undefined
}

/** Props for the PlainText component. */
export interface PlainTextProps {
    /** The contentItem object that holds the text property that needs to render */
    contentItem: Partial<TextContentElement>
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** The classes to apply the styling */
    classes?: string
}

/**
 * An editable component that renders plain text. It uses Typography (caption variant) from Material-UI as a placeholder for CKEditor (ContentEditor).
 * Can be editable based on:
 * - by default, the component is not editable
 * - when the component has changes (initial suggestions)
 * - when focused or clicked
 * @param {PlainTextProps} props - Props for the component.
 */
export const PlainText = memo<PlainTextProps>(
    ({ contentItem, inactive = false, editable = false, classes }) => {
        /** controller props and handlers */
        const {
            text,
            keyedText,
            keyedTextValue,
            formattedContentId,
            initialData,
            initialSuggestions,
            clickCoordinates,
            ckeToggle,
            shouldRenderContentEditor,
            isKeyedText,
            handlers,
            setIsEditingPlainText,
        } = usePlainTextController(contentItem)

        const plainTextStyles = usePlainTextStyles()
        const isEditMode = shouldRenderContentEditor && !inactive && editable

        const displayText = text ?? ''
        const hasEmptyText = !text?.trim()

        // Memoize container class to prevent recreation on each render
        const containerClass = useMemo(() => {
            if (!editable) return plainTextStyles.plainTextContainerReadOnly
            if (hasEmptyText) return plainTextStyles.plainTextContainer
            return plainTextStyles.plainTextContainerWithContent
        }, [editable, hasEmptyText, plainTextStyles])

        // Generate suggestion data attribute
        const suggestionDataAttribute = useMemo(
            () => generateSuggestionDataAttribute(initialSuggestions?.[0]?.id),
            [initialSuggestions]
        )

        return (
            <Typography
                variant="caption"
                key={formattedContentId}
                data-testid={formattedContentId}
                data-suggestion={suggestionDataAttribute}
                className={clsx(classes, containerClass)}
                tabIndex={0}
                ref={ckeToggle.active ? ckeToggle.placeholderRef : null}
                onKeyDown={handlers.onKeyDown}
                onKeyUp={handlers.onKeyUp}
                onFocusCapture={handlers.onFocusCapture}
                onClick={handlers.onClick}
                onMouseMove={handlers.onMouseMove}
            >
                {isEditMode ? (
                    <EditorContent
                        initialData={initialData}
                        initialSuggestions={initialSuggestions}
                        clickCoordinates={clickCoordinates}
                        handlers={handlers}
                        setIsEditingPlainText={setIsEditingPlainText}
                        isKeyedText={isKeyedText}
                        keyedText={keyedText}
                        keyedTextValue={keyedTextValue}
                        classes={classes}
                    />
                ) : (
                    displayText
                )}
                {isKeyedText && !keyedTextValue && !inactive && (
                    <EmptyPlainTextContainer
                        element={{
                            id: `${contentItem.id}${ADDITIONAL_ELEMENT_SUFFIX}`,
                            text: '',
                            type: contentItem.type,
                        }}
                        inactive={inactive}
                        editable={true}
                    />
                )}
            </Typography>
        )
    }
)
